import { hashString } from "@func-core/core/domain/view-models/experiment-view-model";
import { PartialWindow } from "@func-core/core/types";
import { isSessionStorageAvailable } from "@func-core/core/utils/browser-features";
import { rollbar, RollbarLogArgument } from "@utils/rollbar";

export type StoredExperiment = {
  experimentId: string;
  variantId: string;
  isPreQuotes: boolean;
};

export type ExperimentStorage = {
  userHash?: string;
  anonHash?: string;
  assignments: Record<string, StoredExperiment>;
};

type AdapterOptions = {
  window?: PartialWindow & { sessionStorage?: Storage };
  storageKey?: string;
};

export type ExperimentStorageResponse = {
  storeAssignment: (experimentId: string, variantId: string, isPreQuotes: boolean) => void;
  getAssignment: (experimentId: string) => StoredExperiment | null;
  getAllAssignments: () => Record<string, StoredExperiment>;
  storeUserHash: (userKey: string, countryCodeIso: string) => void;
  getUserHash: () => string | undefined;
  storeAnonymousHash: (anonKey: string, countryCodeIso: string) => void;
  getAnonymousHash: () => string | undefined;
  clearAll: () => void;
};

const isWindowDefined = typeof window !== "undefined";

export const createExperimentStorageAdapter = ({
  window: windowObj = isWindowDefined
    ? (window as PartialWindow & { sessionStorage?: Storage })
    : undefined,
  storageKey = "ab_test_assignments",
}: AdapterOptions = {}): ExperimentStorageResponse => {
  const hasSessionStorage = isSessionStorageAvailable();
  const getStorage = (): ExperimentStorage => {
    try {
      if (!hasSessionStorage) return { assignments: {} };

      const storedValue = windowObj?.sessionStorage?.getItem(storageKey);
      if (!storedValue) return { assignments: {} };

      return JSON.parse(storedValue) as ExperimentStorage;
    } catch (error) {
      rollbar.error("Failed to retrieve experiment storage", error as RollbarLogArgument);

      return { assignments: {} };
    }
  };

  const saveStorage = (storage: ExperimentStorage): void => {
    try {
      if (!hasSessionStorage) return;
      windowObj?.sessionStorage?.setItem(storageKey, JSON.stringify(storage));
    } catch (error) {
      rollbar.error("Failed to save experiment storage", error as RollbarLogArgument);
    }
  };

  const storeHash = (hashIdentifier: string, hashKey: string): void => {
    if (!hasSessionStorage) return;

    const storage = getStorage();
    const hash = hashString(hashIdentifier).toString();

    const updatedStorage: ExperimentStorage = {
      ...storage,
      [hashKey]: hash,
    };

    saveStorage(updatedStorage);
  };

  return {
    storeAssignment: (experimentId: string, variantId: string, isPreQuotes: boolean): void => {
      try {
        if (!hasSessionStorage) return;

        const storage = getStorage();
        const updatedStorage: ExperimentStorage = {
          ...storage,
          assignments: {
            ...storage.assignments,
            [experimentId]: {
              experimentId,
              variantId,
              isPreQuotes,
            },
          },
        };

        saveStorage(updatedStorage);
      } catch (error) {
        rollbar.error("Failed to store experiment assignment", error as RollbarLogArgument);
      }
    },

    storeUserHash: (userKey: string, countryCodeIso: string): void => {
      try {
        storeHash(`user-${userKey}-${countryCodeIso}`, "userHash");
      } catch (error) {
        rollbar.error("Failed to store user hash", error as RollbarLogArgument);
      }
    },

    getUserHash: (): string | undefined => {
      try {
        if (!hasSessionStorage) return undefined;

        const storage = getStorage();

        return storage.userHash;
      } catch (error) {
        rollbar.error("Failed to get user hash", error as RollbarLogArgument);

        return undefined;
      }
    },

    storeAnonymousHash: (anonKey: string, countryCodeIso: string): void => {
      try {
        storeHash(`anon-${anonKey}-${countryCodeIso}`, "anonHash");
      } catch (error) {
        rollbar.error("Failed to store anon hash", error as RollbarLogArgument);
      }
    },

    getAnonymousHash: (): string | undefined => {
      try {
        if (!hasSessionStorage) return undefined;

        const storage = getStorage();

        return storage.anonHash;
      } catch (error) {
        rollbar.error("Failed to get anon hash", error as RollbarLogArgument);

        return undefined;
      }
    },

    getAssignment: (experimentId: string): StoredExperiment | null => {
      if (!hasSessionStorage) return null;

      const storage = getStorage();

      return Object.prototype.hasOwnProperty.call(storage.assignments, experimentId)
        ? storage.assignments[experimentId]
        : null;
    },

    getAllAssignments: (): Record<string, StoredExperiment> => {
      if (!hasSessionStorage) return {};

      return getStorage().assignments;
    },

    clearAll: (): void => {
      try {
        if (!hasSessionStorage) return;
        windowObj?.sessionStorage?.removeItem(storageKey);
      } catch (error) {
        rollbar.error("Failed to clear experiment storage", error as RollbarLogArgument);
      }
    },
  };
};
